import { mutationOptions, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type { AnswerRequest, Form } from "../dynamicForm";
import type { CustomHistoryEntry, HistoryEntry } from "../dynamicHistory/types";
import type { Criticality, Score } from "../classification/schemas";
import type {
  CreatedSystem,
  CreateSystemPayload,
  System,
  SystemById,
  SystemsGridView,
  UpdateSystemPayload,
} from "./systems.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { isSameQueryKey } from "./utils";
import { api } from "./apiClient";
import { invalidateAll } from "./invalidation";
import { prefetch } from "./prefetch";
import { queryClient } from "./queryClient";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/systems`;

const systemsKeys = {
  all: () => ["systems"] as const,
  byId: (id: string) => [...systemsKeys.all(), id] as const,
  gridView: () => [...systemsKeys.all(), "gridview"] as const,
  form: () => [...systemsKeys.all(), "form"] as const,
  history: (id: string) => [...systemsKeys.all(), id, "history"] as const,
  selectOptions: () => [...systemsKeys.all(), "select-options"] as const,
};

export type ConnectionType = "stored" | "used";

export type ConnectedInformationType = {
  connectionId: string;
  informationTypeId: string;
  name: string;
  createdAt: string;
  classification?: { criticality?: Criticality | null } | null;
};

export type ConnectedInformationTypesInfo = {
  storedConnections: ConnectedInformationType[];
  usedConnections: ConnectedInformationType[];
};

export type SystemClassification = {
  criticality: Criticality | null;
  override?: { rationale: string; scores: Array<Score> };
};

/** Get System by id */
const systemByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: systemsKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}`) as Promise<SystemById>,
    throwOnError: true,
  });

export const prefetchSystemById = (id: string) => prefetch(systemByIdQueryOptions(id));

export const useGetSystemById = (id: string) => useQuery(systemByIdQueryOptions(id));

/** Get system form */
const systemFormQueryOptions = () =>
  queryOptions({
    queryKey: systemsKeys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/form`) as Promise<Form>,
    throwOnError: true,
  });

export const prefetchSystemForm = () => prefetch(systemFormQueryOptions());

export const useGetSystemForm = () => useQuery(systemFormQueryOptions());

/** Update system answers */
export const useUpdateSystemAnswers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ systemId, answers }: { systemId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT}/${systemId}/answers`, answers) as Promise<SystemById>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Create system */
export const useCreateSystem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSystemPayload) => api.post(BASE_ENDPOINT, payload) as Promise<CreatedSystem>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Delete system */
export const useDeleteSystem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (systemId: string) => api.delete(`${BASE_ENDPOINT}/${systemId}`),
    onSuccess: (_, id) =>
      queryClient.invalidateQueries({
        predicate: query => !isSameQueryKey(query.queryKey, systemsKeys.byId(id)),
      }),
  });
};

export const systemsQueryOptions = () =>
  queryOptions({
    queryKey: systemsKeys.all(),
    queryFn: () => api.get(BASE_ENDPOINT, { schema: z.custom<Array<System>>() }),
    throwOnError: true,
  });

/** Get systems grid view */
const systemsGridViewQueryOptions = () =>
  queryOptions({
    queryKey: systemsKeys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/gridview`) as Promise<SystemsGridView>,
    throwOnError: true,
  });

export const prefetchSystemsGridView = () => prefetch(systemsGridViewQueryOptions());

export const useGetSystemsGridView = () => useQuery(systemsGridViewQueryOptions());

/** Update system */
export const useUpdateSystem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ systemId, payload }: { systemId: string; payload: UpdateSystemPayload }) =>
      api.patch(`${BASE_ENDPOINT}/${systemId}`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

type SystemHistoryCustomEntry =
  | CustomHistoryEntry<"systemName", string>
  | CustomHistoryEntry<
      "systemClassificationOverride",
      { templateKey: string; scores: Array<Score>; rationale: string } | null
    >
  | CustomHistoryEntry<
      "systemConnectedInformationType",
      {
        connectionType: ConnectionType;
        connections: Array<{ informationTypeId: string; informationTypeName: string }>;
      } | null
    >
  | CustomHistoryEntry<"archived", boolean>;

type GetSystemHistoryResponse = {
  history: HistoryEntry<SystemHistoryCustomEntry>[];
};

export const systemHistoryQueryOptions = (id: string) =>
  queryOptions({
    queryKey: systemsKeys.history(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}/history`) as Promise<GetSystemHistoryResponse>,
    throwOnError: true,
  });

const SystemSelectOptionsResponseSchema = z.array(z.object({ id: z.string(), name: z.string() }));

export const systemSelectOptionsQueryOptions = () =>
  queryOptions({
    queryKey: systemsKeys.selectOptions(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/select-options`, { schema: SystemSelectOptionsResponseSchema }),
    throwOnError: true,
  });

export const createSystemInformationTypeConnectionMutationOptions = () =>
  mutationOptions({
    mutationFn: ({
      systemId,
      informationTypeId,
      connectionType,
    }: {
      systemId: string;
      informationTypeId: string;
      informationTypeName: string;
      connectionType: ConnectionType;
    }) => api.post(`${BASE_ENDPOINT}/${systemId}/information-types/${informationTypeId}`, { connectionType }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const deleteSystemInformationTypeConnectionMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ systemId, connectionId }: { systemId: string; connectionId: string }) =>
      api.delete(`${BASE_ENDPOINT}/${systemId}/information-types/${connectionId}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const updateSystemClassificationOverrideMutationOptions = (systemId: string) =>
  mutationOptions({
    mutationFn: ({ scores, rationale }: { scores: Array<Score>; rationale: string }) =>
      api.put(`${BASE_ENDPOINT}/${systemId}/classification-override`, { scores, rationale }),
    onSuccess: () => invalidateAll(),
  });

export const archiveSystemMutationOptions = (id: string) =>
  mutationOptions({
    mutationFn: () => api.patch(`${BASE_ENDPOINT}/${id}/archive`),
    onSuccess: () => invalidateAll(),
  });

export const unarchiveSystemMutationOptions = (id: string) =>
  mutationOptions({
    mutationFn: () => api.patch(`${BASE_ENDPOINT}/${id}/unarchive`),
    onSuccess: () => invalidateAll(),
  });

export type SystemConnectionKind =
  "controllerRegistration" | "processorRegistration" | "informationTypeStored" | "informationTypeUsed";

export type SystemConnectionRow = { id: string; name: string; kind: SystemConnectionKind };

type RepointConnectionType = "Registration" | "ProcessorRegistration" | "InformationType";

const REPOINT_CONNECTION_TYPE: Record<SystemConnectionKind, RepointConnectionType> = {
  controllerRegistration: "Registration",
  processorRegistration: "ProcessorRegistration",
  informationTypeStored: "InformationType",
  informationTypeUsed: "InformationType",
};

const toRepointConnection = ({ id, kind }: SystemConnectionRow) => {
  const type = REPOINT_CONNECTION_TYPE[kind];
  return type === "InformationType" ? { type, connectionId: id } : { type, responseId: id };
};

export const repointSystemConnectionsMutationOptions = (sourceSystemId: string) =>
  mutationOptions({
    mutationFn: ({
      targetSystemId,
      connections,
    }: {
      targetSystemId: string;
      connections: Array<SystemConnectionRow>;
    }) =>
      api.post(`${BASE_ENDPOINT}/${sourceSystemId}/repoint-connections`, {
        targetSystemId,
        connections: connections.map(toRepointConnection),
      }),
    onSuccess: () => invalidateAll(),
  });
